import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl'
import { openNewTab } from '@/shared/utils/openLink'
import _get from 'lodash/get';
import Table, { DateCell, TextCell, TxHashCell } from '@/shared/ui/Table';
import DownloadCsv from '@/entities/DownloadCsv';
import StatusIcon from '@/shared/ui/StatusIcon';
import NewTabLink from '@/shared/ui/NewTabLink';
import LoadMore from '@/shared/ui/LoadMore';
import { formatChartDate } from '@/shared/utils/dateFormatters';
import { 
  SORT_BY,
  getStatusIconVariant,
  getStatusMessage,
} from '~/accountingAndAnonymous/Payouts';
import { useGetBalanceIdQuery, useGetTransactionsPaginatedInfiniteQuery, DEFAULT_TRANSACTIONS_PAGE_SIZE } from '@/shared/api/Payouts';
import { selectPollingInterval } from "@/processes/Settings";
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";
import { ETC_SLICE_VALUE } from '@/shared/constants/payouts';


type PayoutsPoolTableProps = {
  className?: string;
  csvRef?: any;
}

const PayoutsPoolTable = (props: PayoutsPoolTableProps) => {
  const { 
    className,
    csvRef,
  } = props;
  const intl = useIntl();
  const { miner, coin, account } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });
  const pollingInterval = useSelector(selectPollingInterval);

  const isLTCBTC = coin === 'LTCBTC';

  const {
    data: balanceId,
  } = useGetBalanceIdQuery({ account, miner, coin }, { skip: !(account || miner) })

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetTransactionsPaginatedInfiniteQuery({
    coin,
    balanceId,
    account,
    miner,
    isSortingDESC: true,
    orderBy: SORT_BY.CREATED_AT,
    limit: DEFAULT_TRANSACTIONS_PAGE_SIZE,
  }, {
    pollingInterval,
    skip: !coin || balanceId == null,
  });
    
  // Flatten all pages into a single array of transactions
  const transactions = data?.pages?.flat() ?? [];

  const slicedValue = (value: string) => value.slice(0, ETC_SLICE_VALUE);

  const handleLoadMore = (inView: boolean) => { 
    // Fetch next page when user scrolls to the end
    if (inView && !isFetching && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <>
      {/* Inject CSV download in the parent component */}
      {csvRef?.current ? (
        createPortal((
          <DownloadCsv
            prepareData={() => {
              const csvData = [
                [
                  intl.formatMessage({ id: 'payouts.time' }),
                  intl.formatMessage({ id: 'payouts.txid' }),
                  intl.formatMessage({ id: 'payouts.total' }),
                  intl.formatMessage({ id: 'payouts.status' }),
                ],
              ];
              transactions.forEach((transaction: any) => {
                const { value, createdAt, txHash, status } = transaction;
                const statusMessage = intl.formatMessage({ id: getStatusMessage(status) })
                const time = formatChartDate({ date: createdAt, locale: intl.locale });
                return csvData.push([time, txHash, value, statusMessage]);
              });
              return csvData;
            }}
            filename={`${coin} - ${intl.formatMessage({ id: 'payouts.history' })}`}
            title={intl.formatMessage({ id: 'download.csv' })}
          />
        ), csvRef?.current)
      ) : null}

      <Table
        isLoading={isLoading || isFetchingNextPage}
        LoaderComponent={
          <LoadMore title={intl.formatMessage({ id: 'loader.loading.more' })}/>
        }
        onScrollDown={handleLoadMore}
        stickyHeader={true}
        data={transactions}
        responsiveColspan={{
          md: 3,
        }}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: intl.formatMessage({ id: 'payouts.time' }),
            accessor: 'createdAt',
            Cell: DateCell,
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.txid' }),
            accessor: 'txHash',
            disableSortBy: true,
            className: 'hidden md:table-cell',
            Cell: ({ value }: any) => <TxHashCell coin={isLTCBTC ? 'BTC' : coin} hash={value} explorer={explorerLink(value)}/>
          },
          {
            Header: intl.formatMessage({ id: 'payouts.total' }),
            accessor: 'value',
            Cell: ({ value }: any) => <TextCell value={slicedValue(value)} subValue={isLTCBTC ? 'BTC' : coin}/>
          }, 
          {
            Header: intl.formatMessage({ id: 'payouts.status' }),
            accessor: 'status', 
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-between'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                    title={intl.formatMessage({ id: getStatusMessage(value) })}
                    titleClassName='hidden md:inline-block'
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          },
        ]}
      />
    </>
  );
}

export default PayoutsPoolTable;
